import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Circle, FolderOpen, Upload } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { cuando, tamano } from './formato';

const TONO = { VERDE: 'bg-green-50 text-green-800 border-green-200', AMBAR: 'bg-amber-50 text-amber-900 border-amber-200', ROJO: 'bg-red-50 text-red-800 border-red-200' };

/**
 * La pestaña «Documentos» de un registro (persona, producto, lote, pedido,
 * entregable): el expediente se crea solo con el primer archivo y dice
 * qué le falta. `irA` (opcional) abre la carpeta en Archivos.
 */
export default function Expediente({ tipo, referencia, irA }) {
  const [e, setE] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const entrada = useRef(null);

  const [tick, setTick] = useState(0);
  const cargar = useCallback(async () => { setTick((t) => t + 1); }, []);
  useEffect(() => {
    let vivo = true;
    api.get(`/archivos/expedientes/${tipo}/${referencia}`).then((r) => { if (vivo) { setE(r); setError(null); } }, (x) => { if (vivo) setError(x.message); });
    return () => { vivo = false; };
  }, [tipo, referencia, tick]);

  async function subir(lista) {
    setOcupado(true); setAviso(null);
    for (const a of Array.from(lista || [])) {
      try {
        await api.subir(`/archivos/expedientes/${tipo}/${referencia}/subir`, a);
        setAviso(`«${a.name}» guardado en el expediente.`);
      } catch (x) {
        setAviso({ error: x instanceof ErrorApi ? x.message : 'No se pudo subir. Revisa la conexión y vuelve a intentarlo.' });
      }
    }
    setOcupado(false);
    await cargar();
  }

  if (error) return <ErrorConReintento que="No se pudo abrir el expediente" porque={error} onReintentar={cargar} />;
  if (!e) return <Cargando filas={3} etiqueta="Abriendo el expediente" />;
  const falta = e.falta || { documentos: [] };

  return (
    <section className="space-y-3" aria-label={`Expediente de ${e.nombre}`} data-expediente>
      <div className="flex items-center gap-2 flex-wrap">
        <FolderOpen className="h-5 w-5 text-[var(--color-marca)]" aria-hidden="true" />
        <span className="font-semibold text-gray-900">Documentos</span>
        <Ayuda id="archivos.expediente.pantalla" />
        <span className={`text-xs px-2 py-0.5 rounded border ${TONO[falta.semaforo] || TONO.AMBAR}`}>
          {falta.faltan_obligatorios === 0 ? 'Completo' : `Faltan ${falta.faltan_obligatorios} de ${falta.obligatorios} obligatorio(s)`}
        </span>
        {e.solo_lectura && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">Solo lectura</span>}
        {e.existe && irA && <button type="button" className="text-xs text-[var(--color-marca)] font-semibold ml-auto" onClick={() => irA('archivos', { id_carpeta: e.carpeta.id })}>Abrir en Archivos</button>}
      </div>
      {e.dice && <p className="text-sm text-gray-600">{e.dice}</p>}

      <ul className="space-y-1" aria-label="Lo que debe tener">
        {falta.documentos.map((d) => (
          <li key={d.documento} className="flex items-center gap-2 text-sm">
            {d.tiene ? <CheckCircle2 className="h-4 w-4 text-green-700" aria-label="Está" /> : <Circle className="h-4 w-4 text-gray-400" aria-label="Falta" />}
            <span className={d.tiene ? 'text-gray-900' : 'text-gray-700'}>{d.documento}</span>
            {!d.tiene && d.obligatorio && <span className="text-[11px] text-red-700">obligatorio</span>}
            {!d.tiene && !d.obligatorio && <span className="text-[11px] text-gray-500">opcional</span>}
          </li>
        ))}
      </ul>

      {e.archivos.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl" aria-label="Archivos del expediente">
          {e.archivos.map((a) => (
            <li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <span className="flex-1 min-w-0 truncate text-gray-900">{a.nombre}</span>
              <span className="text-xs text-gray-500">{tamano(a.bytes)} · {cuando(a.actualizado_en)}</span>
              <a href={`/api/archivos/${a.id}/descargar`} className="text-xs text-[var(--color-marca)] font-semibold">Descargar</a>
            </li>
          ))}
        </ul>
      )}

      {aviso && (typeof aviso === 'string'
        ? <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2">{aviso}</div>
        : <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{aviso.error}</div>)}

      <div>
        <button type="button" onClick={() => entrada.current?.click()} disabled={ocupado || !e.puede_subir}
                className="bg-[var(--color-marca)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
          <Upload className="h-4 w-4" /> {ocupado ? 'Subiendo…' : 'Subir documento'}
        </button>
        {/* Solo cuando el permiso llega y de verdad es NO (=== false) se
            avisa. Desconocido (todavía sin respuesta) no es negado: el
            botón queda apagado y no aparece ningún «no puedes». */}
        {e.puede_subir === false && <p className="text-xs text-gray-600 mt-1">{e.solo_lectura ? 'Este expediente es de solo lectura: la persona ya no está activa.' : 'Tu rol no sube documentos aquí. Pídele a quien administra tu empresa el permiso «Subir y organizar archivos».'}</p>}
        <input ref={entrada} type="file" multiple className="hidden" aria-label="Subir documento" onChange={(ev) => { subir(ev.target.files); ev.target.value = ''; }} />
      </div>
    </section>
  );
}
